import { useEffect, useMemo, useState } from "react";
import { Copy, ArrowClockwise, ArrowsLeftRight, Package } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useTaxPricing, applyRoundingRule, fmtFuel } from "@/lib/taxPricing";

/**
 * FuelTools — V1.1.1 · OPS-002 · Fuel Calculator Suite.
 *
 * Six calculators sharing a compact tabbed layout. The first tab is the
 * authoritative **Fuel Pricing Calculator** (formerly "Back Out Taxes"
 * + "Add Taxes" as two separate tools) — one bidirectional calculator
 * that supports both operational workflows:
 *   • Selling Price → Base Price   (back out taxes)
 *   • Base Price → Selling Price   (build up taxes)
 *
 * Formula (Base → Sell):
 *   Selling = (Base + OntarioRoadTax + OptionalCarbonTax) × (1 + HST)
 *
 * Formula (Sell → Base):
 *   Base = (Selling − OntarioRoadTax − OptionalCarbonTax) ÷ (1 + HST)
 *
 * Every value (Ontario Road Tax, HST, Federal Carbon Tax, Carbon Enabled,
 * Decimal Precision, Rounding Rule) reads from Tax & Pricing Settings.
 * Never hard-coded — governance OPS-002.
 */
export default function FuelTools() {
  const settings = useTaxPricing();
  const [active, setActive] = useState("pricing");

  const TABS = [
    { key: "pricing",    label: "Fuel Pricing",  render: () => <FuelPricing settings={settings} /> },
    { key: "margin",     label: "Margin",        render: () => <Margin settings={settings} /> },
    { key: "percentage", label: "Percentage",    render: () => <Percentage settings={settings} /> },
    { key: "volume",     label: "Volume Sale",   render: () => <VolumeSale settings={settings} /> },
    { key: "discount",   label: "Discount",      render: () => <Discount settings={settings} /> },
  ];
  const activeTab = TABS.find((t) => t.key === active) || TABS[0];

  return (
    <div data-testid="fuel-tools" className="flex flex-col text-slate-900">
      <div
        data-testid="fuel-tools-tabs"
        className="flex flex-wrap gap-1 border-b border-slate-200 p-1"
      >
        {TABS.map((t) => {
          const on = t.key === active;
          return (
            <button
              key={t.key}
              data-testid={`fuel-tool-tab-${t.key}`}
              onClick={() => setActive(t.key)}
              className={`rounded-md px-2 py-1 text-[11px] font-semibold transition ${
                on ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
              }`}
            >
              {t.label}
            </button>
          );
        })}
      </div>
      <div className="p-3">
        {activeTab.render()}
      </div>
    </div>
  );
}

/* ==========================================================================
   Fuel Pricing Calculator — bidirectional (Selling ↔ Base)

   OPS-002.1 · Product Owner directive 2026-07-03:
   The Product record is the authoritative source of tax applicability;
   Tax & Pricing Settings hold the global rates. The Calculator combines
   both automatically — users MUST select a Product first; taxes are
   NEVER manually toggled here.
   ========================================================================== */
function FuelPricing({ settings }) {
  const [products, setProducts] = useState([]);
  const [productId, setProductId] = useState("");
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [direction, setDirection] = useState("sell_to_base");
  const [input, setInput] = useState("");

  // Load active products for the selector — mandatory selection.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingProducts(true);
      try {
        const r = await api.get("/products", { params: { status: "Active" } });
        if (cancelled) return;
        setProducts(r.data || []);
      } catch {
        if (!cancelled) toast.error("Could not load products");
      } finally {
        if (!cancelled) setLoadingProducts(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const product = useMemo(
    () => products.find((p) => p.id === productId) || null,
    [products, productId],
  );
  const taxFlags = product?.tax_flags || null;

  const dp = settings.decimal_precision;
  // Global rates — always read from Tax & Pricing Settings, never
  // hard-coded and never user-overridable in the calculator.
  const globalRoad = settings.ontario_road_tax || 0;
  const globalCarbon = settings.carbon_tax_enabled ? (settings.federal_carbon_tax || 0) : 0;
  const globalHst = settings.hst_rate || 0;
  // Per-product applicability — the Product record decides which of the
  // global rates get included in this calculation.
  const roadTax = taxFlags?.applies_ontario_road_tax ? globalRoad : 0;
  const carbon  = taxFlags?.applies_federal_carbon_tax ? globalCarbon : 0;
  const hst     = taxFlags?.applies_hst ? globalHst : 0;

  const v = parseFloat(input);
  const hasProduct = !!product;
  let base = NaN, selling = NaN, hstAmount = NaN;

  if (hasProduct && Number.isFinite(v)) {
    if (direction === "sell_to_base") {
      // Base = (Selling − RoadTax − Carbon) / (1 + HST)
      selling = v;
      base = (v - roadTax - carbon) / (1 + hst);
      hstAmount = base * hst;
    } else {
      // Selling = (Base + RoadTax + Carbon) × (1 + HST)
      base = v;
      const preTax = v + roadTax + carbon;
      selling = preTax * (1 + hst);
      hstAmount = preTax * hst;
      selling = applyRoundingRule(selling, settings.selling_price_rounding_rule, dp);
    }
  }

  const flip = () => {
    if (direction === "sell_to_base" && Number.isFinite(base)) {
      setInput(fmtFuel(base, dp));
    } else if (direction === "base_to_sell" && Number.isFinite(selling)) {
      setInput(fmtFuel(selling, dp));
    }
    setDirection(direction === "sell_to_base" ? "base_to_sell" : "sell_to_base");
  };

  const primaryLabel = direction === "sell_to_base" ? "Base Price" : "Selling Price";
  const primaryValue = hasProduct ? fmtFuel(direction === "sell_to_base" ? base : selling, dp) : "—";

  const copy = async () => {
    if (!hasProduct) return;
    try {
      await navigator.clipboard.writeText(primaryValue);
      toast.success(`Copied ${primaryLabel}: ${primaryValue}`);
    } catch {
      toast.error("Could not access clipboard");
    }
  };

  return (
    <div
      data-testid="fuel-pricing-shell"
      data-direction={direction}
      data-product-id={productId || ""}
      className="space-y-2"
    >
      {/* Product selector — mandatory --------------------------------- */}
      <div>
        <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500">
          Product <span className="text-red-500">*</span>
        </label>
        <select
          data-testid="fuel-pricing-product"
          value={productId}
          onChange={(e) => setProductId(e.target.value)}
          className="mt-0.5 w-full rounded-md border border-slate-300 bg-white px-2 py-1 text-sm text-slate-900 focus:border-brand focus:outline-none"
        >
          <option value="">
            {loadingProducts ? "Loading products…" : "Select a product"}
          </option>
          {products.map((p) => (
            <option key={p.id} value={p.id}>{p.product_name}</option>
          ))}
        </select>
        {!hasProduct && !loadingProducts && (
          <div
            data-testid="fuel-pricing-product-required"
            className="mt-1 flex items-center gap-1 text-[10px] font-medium text-red-600"
          >
            <Package size={10} weight="bold" />
            Product selection is required — the calculator uses this to decide which taxes apply.
          </div>
        )}
      </div>

      {/* Direction toggle ---------------------------------------------- */}
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
          Direction
        </div>
        <button
          data-testid="fuel-pricing-flip"
          onClick={flip}
          disabled={!hasProduct}
          className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
          title="Swap direction"
        >
          <ArrowsLeftRight size={11} weight="bold" />
          {direction === "sell_to_base" ? "Selling → Base" : "Base → Selling"}
        </button>
      </div>

      {/* Input --------------------------------------------------------- */}
      <div>
        <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500">
          {direction === "sell_to_base" ? "Selling Price (per L)" : "Base Price (per L)"}
        </label>
        <input
          data-testid="fuel-pricing-input"
          type="number"
          inputMode="decimal"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={direction === "sell_to_base" ? "1.729" : "1.033"}
          step="0.001"
          disabled={!hasProduct}
          className="mt-0.5 w-full rounded-md border border-slate-300 bg-white px-2 py-1 text-right font-mono text-sm text-slate-900 focus:border-brand focus:outline-none disabled:cursor-not-allowed disabled:bg-slate-100"
        />
      </div>

      {/* Rate summary — combines product applicability × global rates -- */}
      <div
        data-testid="fuel-pricing-rate-summary"
        className="rounded-md border border-slate-200 bg-slate-50 p-2 text-[10px] text-slate-600"
      >
        <div className="flex flex-wrap gap-x-3 gap-y-0.5">
          <RateChip
            label="Road Tax"
            value={`$${fmtFuel(globalRoad, dp)}`}
            testid="fuel-pricing-rate-road"
            applies={taxFlags?.applies_ontario_road_tax}
          />
          <RateChip
            label="Carbon Tax"
            testid="fuel-pricing-rate-carbon"
            value={settings.carbon_tax_enabled ? `$${fmtFuel(globalCarbon, dp)}` : "off"}
            applies={taxFlags?.applies_federal_carbon_tax && settings.carbon_tax_enabled}
            muted={!settings.carbon_tax_enabled}
          />
          <RateChip
            label="HST"
            value={`${(globalHst * 100).toFixed(2)}%`}
            testid="fuel-pricing-rate-hst"
            applies={taxFlags?.applies_hst}
          />
          <RateChip
            label="Rounding"
            value={settings.selling_price_rounding_rule.replace("_", " ")}
            testid="fuel-pricing-rate-rounding"
            applies
          />
        </div>
        <div className="mt-1 text-[10px] text-slate-400">
          {hasProduct
            ? "Rates from Tax & Pricing Settings · applicability from the Product record."
            : "Rates from Tax & Pricing Settings — select a product to apply."}
        </div>
      </div>

      {/* Primary result ----------------------------------------------- */}
      <div className="rounded-md border border-slate-200 bg-slate-50 p-2">
        <div className="mb-1 flex items-center justify-between">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
            {primaryLabel}
          </div>
          <div className="flex gap-1">
            <button
              data-testid="fuel-pricing-copy"
              onClick={copy}
              disabled={!hasProduct || !Number.isFinite(v)}
              className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-1.5 py-0.5 text-[10px] font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-40"
              title="Copy result"
            >
              <Copy size={9} weight="bold" /> Copy
            </button>
            <button
              data-testid="fuel-pricing-clear"
              onClick={() => setInput("")}
              className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-1.5 py-0.5 text-[10px] font-semibold text-slate-700 hover:bg-slate-100"
              title="Clear input"
            >
              <ArrowClockwise size={9} weight="bold" /> Clear
            </button>
          </div>
        </div>
        <div
          data-testid="fuel-pricing-primary"
          className="text-right font-mono text-lg font-semibold text-slate-900"
        >
          {primaryValue}
        </div>
      </div>

      {/* Breakdown ---------------------------------------------------- */}
      <dl className="mt-1 space-y-0.5">
        <BreakdownRow testid="fuel-pricing-base" label="Base Price" value={hasProduct ? fmtFuel(base, dp) : "—"} />
        <BreakdownRow
          testid="fuel-pricing-road"
          label={taxFlags?.applies_ontario_road_tax ? "+ Ontario Road Tax" : "+ Ontario Road Tax (n/a for this product)"}
          value={hasProduct ? fmtFuel(roadTax, dp) : "—"}
          dim={hasProduct && !taxFlags?.applies_ontario_road_tax}
        />
        <BreakdownRow
          testid="fuel-pricing-carbon"
          label={
            !settings.carbon_tax_enabled
              ? "+ Federal Carbon Tax (globally off)"
              : taxFlags?.applies_federal_carbon_tax
                ? "+ Federal Carbon Tax"
                : "+ Federal Carbon Tax (n/a for this product)"
          }
          value={hasProduct ? fmtFuel(carbon, dp) : "—"}
          dim={hasProduct && (!settings.carbon_tax_enabled || !taxFlags?.applies_federal_carbon_tax)}
        />
        <BreakdownRow
          testid="fuel-pricing-hst"
          label={
            taxFlags?.applies_hst
              ? `+ HST (${(globalHst * 100).toFixed(2)}%)`
              : "+ HST (n/a for this product)"
          }
          value={hasProduct ? fmtFuel(hstAmount, dp) : "—"}
          dim={hasProduct && !taxFlags?.applies_hst}
        />
        <BreakdownRow
          testid="fuel-pricing-selling"
          label="= Selling Price"
          value={hasProduct ? fmtFuel(selling, dp) : "—"}
          strong
        />
      </dl>
    </div>
  );
}

function RateChip({ label, value, testid, muted, applies }) {
  // `applies` reflects the selected product's tax flag. When false we
  // render the chip in a struck-through / dimmed state so operators
  // instantly see the tax is NOT included in the current calculation.
  const off = applies === false;
  const cls = muted ? "opacity-60" : off ? "opacity-50 line-through" : "";
  return (
    <span data-testid={testid} data-applies={applies !== false} className={cls}>
      <span className="text-slate-500">{label}</span>{" "}
      <span className="font-mono text-slate-800">{value}</span>
    </span>
  );
}

function BreakdownRow({ label, value, testid, strong, dim }) {
  return (
    <div className={`flex items-center justify-between text-[11px] ${dim ? "opacity-50" : ""}`}>
      <dt className="text-slate-500">{label}</dt>
      <dd
        data-testid={testid}
        className={`font-mono ${strong ? "font-semibold text-slate-900" : "text-slate-800"}`}
      >
        {value}
      </dd>
    </div>
  );
}

/* ==========================================================================
   3 · Margin & Markup
   ========================================================================== */
function Margin({ settings }) {
  const [cost, setCost] = useState("");
  const [sell, setSell] = useState("");
  const dp = settings.decimal_precision;

  const c = num(cost), s = num(sell);
  const gp = Number.isFinite(c) && Number.isFinite(s) ? s - c : NaN;
  const margin = Number.isFinite(gp) && s !== 0 ? gp / s : NaN;   // as a %
  const markup = Number.isFinite(gp) && c !== 0 ? gp / c : NaN;   // as a %

  return (
    <ToolShell
      testidRoot="margin"
      inputs={
        <>
          <NumField testid="margin-cost" label="Cost (per L)" value={cost} onChange={setCost} placeholder="1.30" step="0.001" autoFocus />
          <NumField testid="margin-sell" label="Selling Price (per L)" value={sell} onChange={setSell} placeholder="1.729" step="0.001" />
        </>
      }
      results={[
        { testid: "margin-gp",     label: "Gross Profit",      value: fmtFuel(gp, dp) },
        { testid: "margin-margin", label: "Margin",            value: pctString(margin) },
        { testid: "margin-markup", label: "Markup",            value: pctString(markup) },
      ]}
      primaryResult={pctString(margin)}
      primaryLabel="Margin"
      onClear={() => { setCost(""); setSell(""); }}
    />
  );
}

/* ==========================================================================
   4 · Percentage
   ========================================================================== */
function Percentage({ settings }) {
  const [amount, setAmount] = useState("");
  const [pct, setPct] = useState("");
  const dp = settings.decimal_precision;

  const a = num(amount), p = num(pct);
  const pOfA = Number.isFinite(a) && Number.isFinite(p) ? a * (p / 100) : NaN;
  const aIsPctOfB = Number.isFinite(a) && Number.isFinite(p) && p !== 0 ? (a / p) * 100 : NaN;
  const aPlus = Number.isFinite(pOfA) ? a + pOfA : NaN;
  const aMinus = Number.isFinite(pOfA) ? a - pOfA : NaN;

  return (
    <ToolShell
      testidRoot="pct"
      inputs={
        <>
          <NumField testid="pct-amount" label="Amount (A)" value={amount} onChange={setAmount} step="0.01" autoFocus />
          <NumField testid="pct-percent" label="Percent (P) %" value={pct} onChange={setPct} step="0.1" />
        </>
      }
      results={[
        { testid: "pct-of",       label: "P% of A",        value: fmtFuel(pOfA, dp) },
        { testid: "pct-a-plus",   label: "A + P%",         value: fmtFuel(aPlus, dp) },
        { testid: "pct-a-minus",  label: "A − P%",         value: fmtFuel(aMinus, dp) },
        { testid: "pct-a-of-b",   label: "A is what % of P (as denom)", value: pctString(aIsPctOfB / 100) },
      ]}
      primaryResult={fmtFuel(pOfA, dp)}
      primaryLabel="P% of A"
      onClear={() => { setAmount(""); setPct(""); }}
    />
  );
}

/* ==========================================================================
   5 · Volume Sale
   ========================================================================== */
function VolumeSale({ settings }) {
  const [litres, setLitres] = useState("");
  const [price, setPrice] = useState("");
  const [hst, setHst] = useState(String(settings.hst_rate));
  const dp = settings.decimal_precision;

  const L = num(litres), pp = num(price), h = num(hst);
  const sub = Number.isFinite(L) && Number.isFinite(pp) ? L * pp : NaN;
  const tax = Number.isFinite(sub) ? sub * h : NaN;
  const total = Number.isFinite(sub) ? sub + tax : NaN;

  return (
    <ToolShell
      testidRoot="volume"
      inputs={
        <>
          <NumField testid="volume-litres" label="Litres" value={litres} onChange={setLitres} step="1" autoFocus />
          <NumField testid="volume-price"  label="Price per Litre" value={price} onChange={setPrice} step="0.001" />
          <NumField testid="volume-hst"    label="HST Rate (decimal)" value={hst} onChange={setHst} step="0.001" />
        </>
      }
      results={[
        { testid: "volume-sub",   label: "Sale Amount", value: fmtFuel(sub, dp) },
        { testid: "volume-tax",   label: "Tax",         value: fmtFuel(tax, dp) },
        { testid: "volume-total", label: "Total",       value: fmtFuel(total, dp) },
      ]}
      primaryResult={fmtFuel(total, dp)}
      primaryLabel="Total"
      onClear={() => { setLitres(""); setPrice(""); setHst(String(settings.hst_rate)); }}
    />
  );
}

/* ==========================================================================
   6 · Discount
   ========================================================================== */
function Discount({ settings }) {
  const [original, setOriginal] = useState("");
  const [pct, setPct] = useState("");
  const dp = settings.decimal_precision;

  const o = num(original), p = num(pct);
  const discountAmt = Number.isFinite(o) && Number.isFinite(p) ? o * (p / 100) : NaN;
  const final = Number.isFinite(discountAmt) ? o - discountAmt : NaN;
  const savings = discountAmt;

  return (
    <ToolShell
      testidRoot="discount"
      inputs={
        <>
          <NumField testid="discount-original" label="Original Price" value={original} onChange={setOriginal} step="0.01" autoFocus />
          <NumField testid="discount-percent" label="Discount %" value={pct} onChange={setPct} step="0.1" />
        </>
      }
      results={[
        { testid: "discount-amount",  label: "Discount", value: fmtFuel(discountAmt, dp) },
        { testid: "discount-savings", label: "Savings",  value: fmtFuel(savings, dp) },
        { testid: "discount-final",   label: "Final Price", value: fmtFuel(final, dp) },
      ]}
      primaryResult={fmtFuel(final, dp)}
      primaryLabel="Final Price"
      onClear={() => { setOriginal(""); setPct(""); }}
    />
  );
}

/* ==========================================================================
   Shared UI — ToolShell wraps every calculator so they all look identical.
   ========================================================================== */
function ToolShell({ inputs, results, primaryResult, primaryLabel, onClear, testidRoot }) {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(String(primaryResult));
      toast.success(`Copied ${primaryLabel}: ${primaryResult}`);
    } catch {
      toast.error("Could not access clipboard");
    }
  };
  return (
    <div className="space-y-2" data-testid={`${testidRoot}-shell`}>
      <div className="space-y-1.5">{inputs}</div>

      <div className="mt-2 rounded-md border border-slate-200 bg-slate-50 p-2">
        <div className="mb-1 flex items-center justify-between">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
            {primaryLabel}
          </div>
          <div className="flex gap-1">
            <button
              data-testid={`${testidRoot}-copy`}
              onClick={copy}
              className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-1.5 py-0.5 text-[10px] font-semibold text-slate-700 hover:bg-slate-100"
              title="Copy result"
            >
              <Copy size={9} weight="bold" /> Copy
            </button>
            <button
              data-testid={`${testidRoot}-clear`}
              onClick={onClear}
              className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-1.5 py-0.5 text-[10px] font-semibold text-slate-700 hover:bg-slate-100"
              title="Clear inputs"
            >
              <ArrowClockwise size={9} weight="bold" /> Clear
            </button>
          </div>
        </div>
        <div
          data-testid={`${testidRoot}-primary`}
          className="text-right font-mono text-lg font-semibold text-slate-900"
        >
          {primaryResult}
        </div>
      </div>

      <dl className="mt-1 space-y-0.5">
        {results.map((r) => (
          <div key={r.testid} className="flex items-center justify-between text-[11px]">
            <dt className="text-slate-500">{r.label}</dt>
            <dd data-testid={r.testid} className="font-mono text-slate-800">{r.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function NumField({ testid, label, value, onChange, ...rest }) {
  return (
    <div>
      <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500">
        {label}
      </label>
      <input
        data-testid={testid}
        type="number"
        inputMode="decimal"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-0.5 w-full rounded-md border border-slate-300 bg-white px-2 py-1 text-right font-mono text-sm text-slate-900 focus:border-brand focus:outline-none"
        {...rest}
      />
    </div>
  );
}

function num(v) {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : NaN;
}

function pctString(v) {
  if (!Number.isFinite(v)) return "—";
  return `${(v * 100).toFixed(2)}%`;
}
